import { toast } from "react-toastify";
import FileSaver from "file-saver";
import { TabState } from "./tab-state";
import { ResourceLoadingState } from "./resource-loading-state";
import apiClient from "../../../apis/api-client";
import { default as tenantManagementClient } from "../../../apis/tenant-management/tenant-management-client";
import { rebuildQuery } from "../../../../utils/url-utils";
import {
  ProcedureDetailType,
  ProcedureEntity,
  PublishSetting,
  ProcedureType,
} from "../../../domain/procedures/procedure-entity";
import {
  SmartApplyApiError,
  reportAsyncError,
  reportUnexpectedError,
  BaseApplicationService,
  ToastMessage,
} from "@graffer-inc/smart-apply";
import { Procedure } from "../../../domain/proc-management/core/types";
import {
  isNotFoundError,
  isPermissionError,
  isSmartApplyAccessRestrictionError,
  reportProcManagementRelatedError,
} from "../../../domain/proc-management/util/error";
import { AuthType } from "../../../domain/auth/auth";
import {
  buildSmartApplyProcedureUrlPath,
  getGroupIdToQueryProc,
} from "../../../domain/proc-management/util";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";
import { ProcedurePresentation } from "@graffer-inc/cloud-gov-core";
import { CustomPathSettings } from "../../../domain/procedures/custom-path";

export type ProcedureContextProps = {
  isSubmitting: boolean;
  resourceLoadState: ResourceLoadingState;
  tabState: TabState;
  procedure?: ProcedureEntity;
  customPathSettings: CustomPathSettings;
  // hasPresentationはカスタムページの編集機能をテナントユーザーに解放するタイミングで削除すること。
  // 未設定の手続きでも当タブを表示すると問い合わせが増えてしまう懸念があり、
  // それを防ぐ目的でのみこのpropertyを利用する。
  hasPresentation: boolean;
  hasCustomUIDoorPage: boolean;
  orgSlug: string;
  procMsts?: Pick<Procedure, "id" | "name">[];
  authCtx: AuthType["data"];
  setAuthCtx: (authCtx: ProcedureContextProps["authCtx"]) => void;
  resetState: typeof ProcedureDetailService.prototype.resetState;
  switchTab: typeof ProcedureDetailService.prototype.switchTab;

  onCopyCreate: typeof ProcedureDetailService.prototype.onCopyCreate;
  onReturnToDraft: typeof ProcedureDetailService.prototype.onReturnToDraft;
  onPauseAccepting: typeof ProcedureDetailService.prototype.onPauseAccepting;
  onResumeAccepting: typeof ProcedureDetailService.prototype.onResumeAccepting;
  onDraftUpdate: typeof ProcedureDetailService.prototype.onDraftUpdate;
  onGroupAdd: typeof ProcedureDetailService.prototype.onGroupAdd;
  onProcedureUpdate: typeof ProcedureDetailService.prototype.onProcedureUpdate;
  onProcedureDelete: typeof ProcedureDetailService.prototype.onProcedureDelete;
  onPublishSettingUpdate: typeof ProcedureDetailService.prototype.onPublishSettingUpdate;
  onExportProcedure: typeof ProcedureDetailService.prototype.onExportProcedure;
  onUnPublish: typeof ProcedureDetailService.prototype.onUnPublish;
  fetchProcMstBySmartApply: typeof ProcedureDetailService.prototype.fetchProcMstBySmartApply;
  onUpdateProcMaster: typeof ProcedureDetailService.prototype.onUpdateProcMaster;
  onArchive: typeof ProcedureDetailService.prototype.onArchive;
  onUnarchive: typeof ProcedureDetailService.prototype.onUnarchive;
};

export class ProcedureDetailService extends BaseApplicationService<ProcedureContextProps> {
  state = {
    isSubmitting: false,
    resourceLoadState: ResourceLoadingState.noPermission(),
    tabState: TabState.initialize(),
    procedure: undefined as ProcedureEntity | undefined,
    customPathSettings: {},
    hasPresentation: false,
    hasCustomUIDoorPage: false,
    savedProcName: "",
    orgSlug: "",
    currentCustomPath: undefined as string | undefined,
    procMsts: undefined,
    authCtx: undefined,
    setAuthCtx: this.setAuthCtx.bind(this),
    resetState: this.resetState.bind(this),
    onCopyCreate: this.onCopyCreate.bind(this),
    onReturnToDraft: this.onReturnToDraft.bind(this),
    switchTab: this.switchTab.bind(this),
    onProcedureDelete: this.onProcedureDelete.bind(this),
    onExportProcedure: this.onExportProcedure.bind(this),
    onUnPublish: this.onUnPublish.bind(this),
    onPauseAccepting: this.onPauseAccepting.bind(this),
    onResumeAccepting: this.onResumeAccepting.bind(this),
    onProcedureUpdate: this.onProcedureUpdate.bind(this),
    onGroupAdd: this.onGroupAdd.bind(this),
    onDraftUpdate: this.onDraftUpdate.bind(this),
    onPublishSettingUpdate: this.onPublishSettingUpdate.bind(this),
    fetchProcMstBySmartApply: this.fetchProcMstBySmartApply.bind(this),
    onUpdateProcMaster: this.onUpdateProcMaster.bind(this),
    onArchive: this.onArchive.bind(this),
    onUnarchive: this.onUnarchive.bind(this),
  };

  async resetState(id: string, tab?: string) {
    this.setState({
      resourceLoadState: ResourceLoadingState.isLoading(),
    });

    try {
      const { data: proc } = await apiClient.fetchProcedure(id);
      this.setState({
        tabState: TabState.isValid(tab)
          ? TabState.from(tab)
          : TabState.initialize(),
        resourceLoadState: ResourceLoadingState.hasPermission(),
        procedure: proc,
      });
      await this.fetchHasPresentation(id);
      await this.fetchOrgSlug();
      await this.fetchCustomPathSettings(proc.orgId, proc.id);
    } catch (e) {
      if (SmartApplyApiError.is403(e)) {
        this.setState({
          resourceLoadState: ResourceLoadingState.noPermission(),
          procedure: undefined,
        });
        return;
      }
      this.setState({
        resourceLoadState: ResourceLoadingState.isLoadFailed(),
      });
      reportAsyncError(e);
    }
  }

  async onUnPublish(id: string) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.unPublishProcedure(id);
      this.setState({ procedure: proc });
      toast.success("手続きの公開を終了しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportAsyncError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  async onPauseAccepting(id: string) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.pauseAcceptingProcedure(id);
      this.setState({ procedure: proc });
      toast.success("受付を一時停止しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportAsyncError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  async onResumeAccepting(id: string) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.resumeAcceptingProcedure(id);
      this.setState({ procedure: proc });
      toast.success("受付を再開しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportAsyncError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  async onProcedureUpdate(proc: ProcedureType, customPath?: string) {
    try {
      await apiClient.updateProcedure(proc.id, {
        ...proc.draft,
        name: proc.name,
        // TODO 手続き説明: descriptionMarkdown に統合されるので、summary を削除する
        summary: proc.summary,
        // TODO 手続き説明: descriptionMarkdown に統合されるので、detailURL を削除する
        detailURL: proc.detailURL,
        descriptionMarkdown: proc.descriptionMarkdown,
        publishSetting: proc.publishSetting,
        isExcludeFromSearch: proc.isExcludeFromSearch,
        isSkipArrivalMail: proc.isSkipArrivalMail,
        formAccessRestriction: proc.formAccessRestriction,
        isLinkTopPage: proc.isLinkTopPage,
        updatedAt: proc.updatedAt,
        customPath: customPath,
      });
      await this.resetState(
        this.state.procedure!.id,
        this.state.tabState.getValue(),
      );
      toast.success("手続き情報を更新しました。");
    } catch (e) {
      if (SmartApplyApiError.isVersionOutdated(e)) {
        toast.error(
          "手続きの更新に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      if (SmartApplyApiError.customUrlExists(e)) {
        toast.error("URLが重複するため、登録できませんでした。");
        return;
      }
      if (SmartApplyApiError.notFoundFormulaUsedInPayment(e)) {
        toast.error(
          "決済設定で無効な数式IDが指定されています。設定内容をご確認ください。",
        );
        return;
      }
      toast.error("手続きの更新に失敗しました。");
      reportUnexpectedError(e);
    }
  }

  async onPublishSettingUpdate(
    setting: PublishSetting,
    withPublish: boolean,
    isPublishNow: boolean,
    isStartAcceptAtPublish: boolean,
  ) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.updateProcedurePublishSetting(
        this.state.procedure!.id,
        setting,
        isPublishNow,
        isStartAcceptAtPublish,
      );
      if (withPublish) {
        const { data: proc } = await apiClient.publishProcedure(
          this.state.procedure!.id,
        );
        this.setState({ procedure: proc });
        toast.success("手続きの設定を確定しました。");
      } else {
        this.setState({ procedure: proc });
        toast.success("公開・受付日時を更新しました。");
      }
    } catch (e) {
      if (SmartApplyApiError.isVersionOutdated(e)) {
        toast.error(
          "手続きの更新に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportUnexpectedError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  async onExportProcedure() {
    if (this.procedure === undefined) return;
    this.setIsSubmitting(true);
    try {
      const { data } = await apiClient.exportEncryptedProcedureSettings(
        this.procedure.id,
      );
      FileSaver.saveAs(new Blob([data]), `${this.procedure.name}.zip`);
    } catch (e) {
      reportUnexpectedError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  async onDraftUpdate(detail: ProcedureDetailType) {
    this.onProcedureUpdate({
      ...this.state.procedure!,
      draft: detail,
    });
  }

  async onCopyCreate(newName: string): Promise<ProcedureEntity> {
    try {
      const { data } = await apiClient.copyProcedure(
        this.procedure.id,
        newName,
      );
      return data;
    } catch (e) {
      reportUnexpectedError(e);
      throw e;
    }
  }

  async onReturnToDraft() {
    try {
      const { data: proc } = await apiClient.returnToDraftProcedure(
        this.state.procedure!.id,
      );
      this.setState({ procedure: proc });
      toast.success("下書きに戻しました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("下書きへの更新に失敗しました。");
    }
  }

  async onProcedureDelete() {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      await apiClient.deleteProcedure(procedure.id);
      toast.success("手続きを削除しました");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("手続きの削除に失敗しました");
    }
  }

  async onGroupAdd(groupId: string) {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      await apiClient.addGroupToProcedure(procedure.id, groupId);
      await this.resetState(procedure.id);
      toast.success("管理グループを設定しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportAsyncError(e);
    }
  }

  switchTab(tabState: TabState) {
    this.history.push(rebuildQuery({ tab: tabState.getValue() }));
    this.setState({
      tabState: tabState,
    });
  }

  setIsSubmitting(isSubmitting: boolean) {
    this.setState({
      isSubmitting: isSubmitting,
    });
  }

  private async fetchHasPresentation(id: string) {
    try {
      const { data } = await apiClient.fetchProcedurePresentation(id);
      const presentation = ProcedurePresentation.fromJSON(data);
      this.setState({
        hasPresentation: !presentation.isEmpty(),
        hasCustomUIDoorPage: !!presentation.doorPageMarkdown,
      });
    } catch (e) {
      if (!SmartApplyApiError.is404(e)) {
        reportAsyncError(e);
      }
    }
  }

  private async fetchOrgSlug() {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      const { data: org } =
        await tenantManagementClient.fetchOrgWithUsingFeature(procedure.orgId);
      this.setState({
        orgSlug: org.getValue().slug,
      });
    } catch (e) {
      toast.error("テナント情報の取得に失敗しました。");
      reportUnexpectedError(e);
    }
  }

  private get procedure() {
    return this.state.procedure!;
  }

  async fetchProcMstBySmartApply(smartApplyId: string, orgId: string) {
    const groupResult = getGroupIdToQueryProc(this.state.authCtx);
    if (!groupResult.canQuery) {
      toast.error(groupResult.message);
      return;
    }
    try {
      const { data: procs } = await apiClient.procManagement.listProcs({
        organizationId: orgId,
        smartApplyId: smartApplyId,
        groupIds: groupResult.groupIds,
      });
      this.setState({
        procMsts: procs.items,
      });
    } catch (e) {
      reportProcManagementRelatedError(e);
      if (isNotFoundError(e)) {
        toast.error(`手続きマスタの取得に失敗しました。`);
      } else if (isPermissionError(e)) {
        toast.error(`手続きマスタを取得する権限がありません。`);
      } else {
        toast.error(
          `手続きマスタの取得に失敗しました。電波状況のよい場所でリロードしてください。`,
        );
      }
    }
  }

  async onUpdateProcMaster(procMstId: string) {
    try {
      const procedure = this.state.procedure;
      if (!procedure) {
        return;
      }
      let currentPath: string | undefined;
      const res = await runGqlQueryV2(() =>
        gqlClient.currentCustomPaths(
          { orgId: procedure.orgId, procedureId: procedure.id },
          gqlHeader("q"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data) {
        currentPath = res.data.currentCustomPaths.currentCustomPath?.customPath;
      }
      const { data: proc } = await apiClient.procManagement.find(procMstId);
      const onLineInfo = proc.onlineInfo;
      const rejectUpdate =
        proc.contentStatus === "PUBLISHED" && !proc.acceptAppOnline;
      if (rejectUpdate) {
        toast.error(
          `対象の手続きマスタは電子申請を受付けない状態で公開されているため、この手続きを掲載することはできません。`,
        );
        return;
      }
      proc.onlineInfo = {
        ...onLineInfo,
        applicants: onLineInfo ? onLineInfo.applicants : [],
        grafferSmartApplyInfo: {
          procedureId: procedure.id,
          procedureName: procedure.name,
          procedurePath: buildSmartApplyProcedureUrlPath(
            procedure.id,
            this.state.orgSlug,
            currentPath,
          ),
        },
      };
      proc.acceptAppOnline = true;
      const { data: newProc } = await apiClient.procManagement.update(proc);
      this.setState({
        // 手続きマスタ:スマート申請 = 1:1という前提
        procMsts: [newProc],
      });
      toast.success(`この手続きのリンクを手続きマスタに掲載しました。`);
    } catch (e) {
      // ありうる操作なので、reportしない
      if (isSmartApplyAccessRestrictionError(e)) {
        toast.error(
          "パスコード設定を無効化した状態で一度保存したあとで、再度、手続きマスタを選択してください。",
        );
        return;
      }

      reportProcManagementRelatedError(e);
      if (isNotFoundError(e)) {
        toast.error(`手続きマスタの更新に失敗しました。`);
      } else if (isPermissionError(e)) {
        toast.error(`手続きマスタを更新する権限がありません。`);
      } else {
        toast.error(
          `手続きマスタ一覧の更新に失敗しました。電波状況のよい場所でリロードしてください。`,
        );
      }
    }
  }

  async onArchive() {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      const updatedAtUnixTime = new Date(procedure.updatedAt).getTime() / 1000;
      const result = await runGqlQueryV2(() =>
        gqlClient.ArchiveProcedure(
          {
            procedureId: procedure.id,
            previousUpdatedAtUnixTime: updatedAtUnixTime,
          },
          gqlHeader("m"),
        ),
      );
      if (result.error) {
        if (
          result.matchError(400, "bad_request.not_complete_all_applications")
        ) {
          toast.error("手続きに未完了の申請があるため、アーカイブできません。");
          return;
        }
        throw result.error.rawError;
      }
      toast.success("手続きをアーカイブしました。");
      const { data: proc } = await apiClient.fetchProcedure(procedure.id);
      this.setState({ procedure: proc });
    } catch (e) {
      reportAsyncError(e);
      toast.error("手続きのアーカイブに失敗しました。");
      return;
    }
  }

  async onUnarchive() {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      const updatedAtUnixTime = new Date(procedure.updatedAt).getTime() / 1000;
      const result = await runGqlQueryV2(() =>
        gqlClient.UnarchiveProcedure(
          {
            procedureId: procedure.id,
            previousUpdatedAtUnixTime: updatedAtUnixTime,
          },
          gqlHeader("m"),
        ),
      );
      if (result.error) {
        throw result.error.rawError;
      }
      toast.success("手続きを復元しました。");
      const { data: proc } = await apiClient.fetchProcedure(procedure.id);
      this.setState({ procedure: proc });
    } catch (e) {
      reportAsyncError(e);
      toast.error("手続きの復元に失敗しました。");
      return;
    }
  }

  setAuthCtx(authCtx: ProcedureContextProps["authCtx"]) {
    this.setState((prev) => {
      return {
        ...prev,
        authCtx,
      };
    });
  }

  private async fetchCustomPathSettings(orgId: string, procedureId: string) {
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.currentCustomPaths(
          { orgId: orgId, procedureId: procedureId },
          gqlHeader("q"),
        ),
      );
      if (res.error) {
        throw res.error.rawError;
      }
      if (res.data) {
        const currentPaths = res.data.currentCustomPaths;
        this.setState({
          customPathSettings: {
            currentPath: currentPaths.currentCustomPath?.customPath,
            reserved: currentPaths.nextCustomPath?.customPath
              ? {
                  path: currentPaths.nextCustomPath.customPath,
                  startDatetime: currentPaths.nextCustomPath?.startUnixtime
                    ? new Date(
                        currentPaths.nextCustomPath?.startUnixtime * 1000,
                      )
                    : undefined,
                }
              : undefined,
          },
        });
        return;
      }
      throw new Error(
        `failed to fetch custom paths for orgId: ${orgId} and procedureId: ${procedureId}`,
      );
    } catch (e) {
      toast.error("URLのカスタマイズ情報の取得に失敗しました。");
      reportUnexpectedError(e);
    }
  }
}
